import { useEffect, useState } from "react";
import axios from "axios";
import "./get-appointed.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINES = [
    "General Liability",
    "Participant Accident",
    "Sexual Abuse & Molestation",
    "Commercial Property",
    "Excess GL",
    "Fitness Specialty",
];
const VERTICALS = ["Amateur Sport", "Collegiate", "K-12", "Facilities & Fitness"];
const PREMIUM_BANDS = ["Under $250K", "$250K – $1M", "$1M – $5M", "$5M+"];
const SOURCES = ["Referral", "Industry event", "LinkedIn / social", "Existing relationship", "Other"];

const REQUIRED_FIELDS = [
    "agent_name", "agency_name", "email", "phone",
    "address1", "city", "state", "zip", "states_licensed",
];

const INITIAL_FORM = {
    agent_name: "", title: "", agency_name: "", email: "", phone: "",
    address1: "", city: "", state: "", zip: "", website: "",
    states_licensed: "", npn: "",
    lines: [], verticals: [],
    premium_band: "", source: "", notes: "",
    consent: false,
};

export default function GetAppointed() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    // SEO + page title for the public landing page (overrides the global app title)
    useEffect(() => {
        const prev = document.title;
        document.title = "Get Appointed — Players Health MGA | Broker Partnership";
        return () => { document.title = prev; };
    }, []);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: false }));
    };

    const toggleArr = (key, value) => {
        setForm((f) => {
            const arr = f[key] || [];
            return { ...f, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setServerError("");
        // Client-side required validation (mirrors backend constraints)
        const nextErrors = {};
        REQUIRED_FIELDS.forEach((k) => {
            if (!String(form[k] || "").trim()) nextErrors[k] = true;
        });
        if (!form.consent) nextErrors.consent = true;
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) {
            // Scroll the first error into view
            const first = Object.keys(nextErrors)[0];
            const el = document.querySelector(`[name="${first}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, form);
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setServerError(
                typeof detail === "string"
                    ? detail
                    : "Something went wrong submitting your request. Please email submissions@playershealth.com or try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="ph-landing">
                <nav>
                    <div className="wrap">
                        <a className="brand" href="#top">
                            <img src="/players-health-logo.png" alt="Players Health" className="logo" />
                        </a>
                    </div>
                </nav>
                <section style={{ minHeight: "70vh", display: "flex", alignItems: "center", padding: "120px 28px", background: "var(--dark)", color: "#fff" }}>
                    <div className="wrap" style={{ textAlign: "center" }}>
                        <span className="chip">Request received</span>
                        <h1 style={{ fontSize: 56, fontWeight: 900, margin: "20px 0 14px" }}>
                            Thanks. <span className="gtext">We'll be in touch.</span>
                        </h1>
                        <p style={{ fontSize: 18, color: "#d7dae1", maxWidth: 640, margin: "0 auto 32px" }}>
                            Your appointment request is in. A member of our underwriting partnerships team will reach out shortly with your producer agreement and your named underwriter.
                        </p>
                        <a className="btn btn-primary" href="https://playershealth.com" data-testid="landing-back-to-site-btn">Back to playershealth.com</a>
                    </div>
                </section>
                <div className="rule-warm"></div>
                <footer className="landing">
                    <div className="wrap">
                        <div className="brand-row">
                            <img src="/players-health-logo.png" alt="Players Health" className="logo" />
                        </div>
                        <span>MGA Broker Overview · 2026 · Playershealth.com</span>
                    </div>
                </footer>
            </div>
        );
    }

    return (
        <div className="ph-landing" data-testid="get-appointed-page">
            <nav>
                <div className="wrap">
                    <a className="brand" href="#top">
                        <img src="/players-health-logo.png" alt="Players Health" className="logo" />
                    </a>
                    <div className="navlinks">
                        <a href="#value">Why Partner</a>
                        <a href="#coverage">Coverage</a>
                        <a href="#appetite">Appetite</a>
                        <a href="#submit">Submissions</a>
                        <a className="nav-cta" href="#appoint">Get Appointed</a>
                    </div>
                </div>
            </nav>

            {/* HERO */}
            <header className="hero" id="top">
                <div className="wrap">
                    <div>
                        <span className="chip">Broker Partnership · 2026</span>
                        <h1>Partner with the MGA.<br /><span className="gtext">Built for safer sport.</span></h1>
                        <p className="lead">A specialist MGA for amateur sport, fitness and recreation — built for brokers who want to be sharper risk advisors and faster to the bind. Bring us your amateur sport and facilities accounts; we bring the underwriting, the platform, and the risk-management toolkit.</p>
                        <div className="cta-row">
                            <a className="btn btn-primary" href="#appoint">Get Appointed</a>
                            <a className="btn btn-ghost" href="#value">See the Advantage</a>
                        </div>
                        <div className="verticals">
                            <span>Amateur Sport</span><span>Collegiate</span><span>K–12</span><span>Facilities &amp; Fitness</span>
                        </div>
                    </div>
                    <div className="hero-card">
                        <h3>A specialist edge for your book</h3>
                        <p className="sub">Why brokers place with Players Health</p>
                        <div className="stat-grid">
                            <div className="stat"><div className="n">11+</div><div className="l">Years in sport &amp; fitness</div></div>
                            <div className="stat"><div className="n">A-Rated</div><div className="l">Admitted carrier paper</div></div>
                            <div className="stat"><div className="n">50/50</div><div className="l">All-states licensed</div></div>
                            <div className="stat"><div className="n">Multi-Line</div><div className="l">Coverage under one roof</div></div>
                        </div>
                    </div>
                </div>
                <div className="rule-warm"></div>
            </header>

            {/* VALUE */}
            <section className="block" id="value">
                <div className="wrap">
                    <div className="seclabel"><span className="chip">Broker Value</span></div>
                    <h2 className="sechead">More than an MGA. A <span className="gtext">competitive edge</span> for your book.</h2>
                    <div className="underline"></div>
                    <p className="secsub" style={{ marginTop: 18 }}>Brokers placing with Players Health get a specialist underwriting team, a digital placement experience, and risk-management resources their accounts can't get anywhere else.</p>
                    <div className="cards">
                        <div className="card"><div className="num">01</div><h3>Specialist Underwriting</h3><p>Sports and fitness is all we do. Submissions are reviewed by underwriters who know the exposures cold — not generalists working through a queue.</p></div>
                        <div className="card"><div className="num">02</div><h3>Digital Quote &amp; Bind</h3><p>A streamlined indication and quote workflow for in-appetite risks, and one portal for the full submission, bind, and renewal lifecycle.</p></div>
                        <div className="card"><div className="num">03</div><h3>Risk Resources Included</h3><p>Background checks, SAM protocols, concussion policies, waivers and coach training — built-in tools your accounts can adopt the day they bind.</p></div>
                        <div className="card"><div className="num">04</div><h3>Coverage Under One Roof</h3><p>GL, Participant Accident, SAM, Property and the new admitted GL/Property lines — placed and serviced together, with one underwriting relationship.</p></div>
                    </div>
                </div>
            </section>

            {/* APPROACH */}
            <section className="block light">
                <div className="wrap">
                    <div className="seclabel"><span className="chip">Broker Playbook</span></div>
                    <h2 className="sechead">Help your accounts <span className="gtext">see, manage and transfer</span> risk the right way.</h2>
                    <div className="underline"></div>
                    <p className="secsub" style={{ marginTop: 18 }}>Four moves that turn a renewal into a real risk-advisory conversation — every time.</p>
                    <div className="approach">
                        <div className="step"><div className="k">SEE</div><h4>See the Risk Clearly</h4><p>Walk every account through hazard class, participant exposure, prior losses and program gaps — before talking price.</p></div>
                        <div className="step"><div className="k">MITIGATE</div><h4>Mitigate Intentionally</h4><p>Plug them into our risk-management tools: background checks, SAM protocols, concussion policy, coach training.</p></div>
                        <div className="step"><div className="k">TRANSFER</div><h4>Transfer Wisely</h4><p>Match the right limits across GL, Participant Accident, SAM and Property. No over-buying; no surprise gaps.</p></div>
                        <div className="step"><div className="k">SERVE</div><h4>Serve with Conviction</h4><p>Stay engaged after bind — claims advocacy, mid-term changes, renewal prep and ongoing safety partnership.</p></div>
                    </div>
                    <div className="quote">
                        <p>“We're not in the business of selling a commodity. We're in the business of helping organizations protect athletes, strengthen operations, and preserve the experience of sport.”</p>
                        <div className="by">— Players Health</div>
                    </div>
                </div>
            </section>

            {/* COVERAGE */}
            <section className="block" id="coverage">
                <div className="wrap">
                    <div className="seclabel"><span className="chip">Lines &amp; Limits</span></div>
                    <h2 className="sechead">Coverage placed through <span className="gtext">admitted A-rated carriers.</span></h2>
                    <div className="underline"></div>
                    <p className="secsub" style={{ marginTop: 18 }}>One submission, one bind, one underwriting relationship — across the full program.</p>
                    <div className="cov">
                        <div className="item"><div className="tag">Liability · Sirius Point / Spinnaker</div><h4>Commercial General Liability</h4><ul><li>Up to $5M Occurrence / $5M Aggregate</li><li>Products &amp; Completed Ops up to $3M</li><li>Participant Liability up to $1M / $1M</li></ul></div>
                        <div className="item"><div className="tag">Participants · Sirius Point</div><h4>Participant Accident</h4><ul><li>$25,000 minimum, amateur sport</li><li>Limits available up to $100,000</li><li>Excess medical, AD&amp;D, dental</li></ul></div>
                        <div className="item"><div className="tag">Abuse · GL sub-limit</div><h4>Sexual Abuse &amp; Molestation</h4><ul><li>Sub-limit on the GL policy</li><li>Up to $1M Occ / $2M Aggregate</li><li>Paired with SAM protocol library</li></ul></div>
                        <div className="item"><div className="tag">Property · New · Spinnaker / GAIC / Aspen</div><h4>Commercial Property</h4><ul><li>Spinnaker up to $5M TIV / location</li><li>Great American up to $1M TIV</li><li>Aspen Inland Marine up to $6M TIV</li></ul></div>
                        <div className="item"><div className="tag">Fitness · Studio &amp; club endorsements</div><h4>Fitness Specialty Lines</h4><ul><li>Professional Liability to limit</li><li>Hired &amp; Non-Owned Auto up to $1M</li><li>Medical Payment up to $25,000</li></ul></div>
                        <div className="item"><div className="tag">New · 7/1 Launch</div><h4>Excess GL (Non-Admitted)</h4><ul><li>Occurrence up to $5M</li><li>Aggregate up to $5M</li><li>Products &amp; Completed Ops up to $3M</li></ul></div>
                    </div>
                </div>
            </section>

            {/* APPETITE */}
            <section className="block light" id="appetite">
                <div className="wrap">
                    <div className="seclabel"><span className="chip">Appetite — at a glance</span></div>
                    <h2 className="sechead">Four verticals. <span className="gtext">One specialist MGA.</span></h2>
                    <div className="underline"></div>
                    <p className="secsub" style={{ marginTop: 18 }}>A working framework, not a binding appetite. Every account is reviewed individually — referrals and exceptions are welcome.</p>
                    <div className="appetite">
                        <div className="ap a"><div className="lvl">Class A · Broadest · Low Hazard</div><h4>Our Deepest Capacity</h4><p>Fast indications for in-appetite submissions: endurance &amp; running, racquet sports, golf, dance &amp; yoga, fitness &amp; personal training, and low-contact team formats.</p></div>
                        <div className="ap b"><div className="lvl">Class B · Core Book · Medium Hazard</div><h4>The Core of Amateur Sport</h4><p>Active appetite with structured safety protocols and credible loss history: baseball &amp; softball, basketball, soccer, volleyball, cheer, swimming, martial arts and more.</p></div>
                        <div className="ap c"><div className="lvl">Class C · Refer Early · High Hazard</div><h4>Case-by-Case</h4><p>Best fit with documented protocols, mature programs and clean loss history: contact football, hockey, lacrosse, rugby, wrestling, gymnastics, skiing. Bring underwriting in early.</p></div>
                    </div>
                </div>
            </section>

            {/* CHECKLIST */}
            <section className="block" id="submit">
                <div className="wrap">
                    <div className="seclabel"><span className="chip">What we need</span></div>
                    <h2 className="sechead">The <span className="gtext">clean-file checklist.</span></h2>
                    <div className="underline"></div>
                    <p className="secsub" style={{ marginTop: 18 }}>Players Health rates on participant counts — getting the breakdown right at submission is the single biggest accelerator of a clean, accurate quote.</p>
                    <div className="checklist">
                        <div className="chk"><div className="n">01</div><h4>5-Year Loss Runs</h4><p>Currently valued. If unavailable, a signed no-known-loss letter on the insured's letterhead, subject to underwriting approval.</p></div>
                        <div className="chk"><div className="n">02</div><h4>SAM Questionnaire</h4><p>Confirm background checks on all adults, provide SAM prevention protocols and procedures, and disclose any prior SAM claims.</p></div>
                        <div className="chk"><div className="n">03</div><h4>Participant Counts</h4><p>By age group (12 &amp; under · 13–15 · 16–18 · 19+) plus total managers, coaches, referees and officials.</p></div>
                        <div className="chk"><div className="n">04</div><h4>Amateur Sports Supplemental</h4><p>Leagues, tournaments, clinics, camps and events — participants and days for each.</p></div>
                        <div className="chk"><div className="n">05</div><h4>Facilities Supplemental</h4><p>Locations, membership types, day passes, classes and event details — participants by age group for each.</p></div>
                        <div className="chk"><div className="n">06</div><h4>Additional Exposures</h4><p>Retail and liquor sales, batting cages, tanning beds, pools, climbing walls — captured in our supplemental application.</p></div>
                    </div>
                </div>
            </section>

            {/* APPOINTMENT FORM */}
            <section id="appoint">
                <div className="wrap">
                    <div className="form-grid">
                        <div>
                            <span className="chip">Let's get to work</span>
                            <h2>Get appointed<br />with the MGA.</h2>
                            <p className="secsub">Tell us about your agency and we'll get the appointment process started. A member of our underwriting partnerships team will reach out with next steps, your producer agreement, and your named underwriter.</p>
                            <div className="contactlist">
                                <div className="ci"><span className="dot"></span><div><div className="lbl">Submissions</div><div className="val">submissions@playershealth.com</div></div></div>
                                <div className="ci"><span className="dot"></span><div><div className="lbl">Broker Portal</div><div className="val">mga.playershealth.com</div></div></div>
                                <div className="ci"><span className="dot"></span><div><div className="lbl">Talk to Underwriting</div><div className="val">Reach out to your named underwriter</div></div></div>
                            </div>
                        </div>

                        <div className="formcard">
                            <div className="fc-head">Broker Appointment Request</div>
                            <div className="fc-sub">Fields marked <span style={{ color: "#E040FB" }}>*</span> are required.</div>
                            {serverError && <div className="err-banner" data-testid="landing-error-banner">{serverError}</div>}
                            <form onSubmit={submit} noValidate data-testid="landing-appointment-form">
                                <div className="frow">
                                    <div className="field">
                                        <label>Agent / Producer Name <span className="req">*</span></label>
                                        <input name="agent_name" value={form.agent_name} onChange={(e) => set("agent_name", e.target.value)} className={errors.agent_name ? "input-error" : ""} data-testid="landing-agent-name" />
                                    </div>
                                    <div className="field">
                                        <label>Title / Role</label>
                                        <input name="title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Producer, Principal" data-testid="landing-title" />
                                    </div>
                                </div>
                                <div className="field full">
                                    <label>Agency Name <span className="req">*</span></label>
                                    <input name="agency_name" value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} className={errors.agency_name ? "input-error" : ""} data-testid="landing-agency-name" />
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>Work Email <span className="req">*</span></label>
                                        <input type="email" name="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={errors.email ? "input-error" : ""} data-testid="landing-email" />
                                    </div>
                                    <div className="field">
                                        <label>Phone <span className="req">*</span></label>
                                        <input type="tel" name="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={errors.phone ? "input-error" : ""} data-testid="landing-phone" />
                                    </div>
                                </div>
                                <div className="field full">
                                    <label>Agency Address <span className="req">*</span></label>
                                    <input name="address1" value={form.address1} onChange={(e) => set("address1", e.target.value)} placeholder="Street address" className={errors.address1 ? "input-error" : ""} data-testid="landing-address1" />
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>City <span className="req">*</span></label>
                                        <input name="city" value={form.city} onChange={(e) => set("city", e.target.value)} className={errors.city ? "input-error" : ""} data-testid="landing-city" />
                                    </div>
                                    <div className="field">
                                        <label>State <span className="req">*</span></label>
                                        <input name="state" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. MN" className={errors.state ? "input-error" : ""} data-testid="landing-state" />
                                    </div>
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>ZIP <span className="req">*</span></label>
                                        <input name="zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} className={errors.zip ? "input-error" : ""} data-testid="landing-zip" />
                                    </div>
                                    <div className="field">
                                        <label>Agency Website</label>
                                        <input name="website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="landing-website" />
                                    </div>
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>States Licensed In <span className="req">*</span></label>
                                        <input name="states_licensed" value={form.states_licensed} onChange={(e) => set("states_licensed", e.target.value)} placeholder="e.g. MN, WI, IA — or 'All 50'" className={errors.states_licensed ? "input-error" : ""} data-testid="landing-states-licensed" />
                                    </div>
                                    <div className="field">
                                        <label>National Producer Number (NPN)</label>
                                        <input name="npn" value={form.npn} onChange={(e) => set("npn", e.target.value)} data-testid="landing-npn" />
                                    </div>
                                </div>
                                <div className="field full">
                                    <label>Lines of Business You Write <span className="req">*</span></label>
                                    <div className="checks">
                                        {LINES.map((l) => (
                                            <label key={l}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.lines.includes(l)}
                                                    onChange={() => toggleArr("lines", l)}
                                                    data-testid={`landing-line-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                /> {l}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="field full">
                                    <label>Verticals You Serve</label>
                                    <div className="checks">
                                        {VERTICALS.map((v) => (
                                            <label key={v}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.verticals.includes(v)}
                                                    onChange={() => toggleArr("verticals", v)}
                                                    data-testid={`landing-vertical-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                /> {v}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>Approx. Sport / Fitness Premium You Place Annually</label>
                                        <select name="premium_band" value={form.premium_band} onChange={(e) => set("premium_band", e.target.value)} data-testid="landing-premium-band">
                                            <option value="">Select…</option>
                                            {PREMIUM_BANDS.map((p) => <option key={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    <div className="field">
                                        <label>How Did You Hear About Us?</label>
                                        <select name="source" value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="landing-source">
                                            <option value="">Select…</option>
                                            {SOURCES.map((s) => <option key={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="field full">
                                    <label>Anything else we should know?</label>
                                    <textarea name="notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Target accounts, timeline, current markets…" data-testid="landing-notes" />
                                </div>
                                <label className={`consent ${errors.consent ? "input-error" : ""}`} style={errors.consent ? { color: "#E040FB" } : {}}>
                                    <input
                                        type="checkbox"
                                        name="consent"
                                        checked={form.consent}
                                        onChange={(e) => set("consent", e.target.checked)}
                                        data-testid="landing-consent"
                                    />
                                    I'm an appointed/licensed insurance producer and consent to Players Health contacting me about appointment. <span className="req">*</span>
                                </label>
                                <button className="submit" type="submit" disabled={submitting} data-testid="landing-submit-btn">
                                    {submitting ? "Sending…" : "Request Appointment"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
                <div className="rule-warm" style={{ position: "absolute", bottom: 0, left: 0 }}></div>
            </section>

            <footer className="landing">
                <div className="wrap">
                    <div className="brand-row">
                        <img src="/players-health-logo.png" alt="Players Health" className="logo" />
                    </div>
                    <span>MGA Broker Overview · 2026 · Playershealth.com</span>
                </div>
            </footer>
        </div>
    );
}
